// apps/landing-page/src/components/ui/Ribbon.tsx
import type { LucideIcon } from 'lucide-react';

type RibbonTone = 'free' | 'discount' | 'bundle';

// `tab` is the visible label, `fold` the darker corner that makes the tab read as
// wrapped around the card edge. Orange is reserved for "gratis" in this brand.
const toneStyles: Record<RibbonTone, { tab: string; fold: string }> = {
  free: { tab: 'bg-orange-500', fold: 'bg-orange-700' },
  discount: { tab: 'bg-red-500', fold: 'bg-red-700' },
  bundle: { tab: 'bg-teal-700', fold: 'bg-teal-900' },
};

interface RibbonProps {
  label: string;
  tone: RibbonTone;
  icon?: LucideIcon;
}

/**
 * Corner tab for a product card. It overhangs the card's right edge by 6px, so it
 * must be rendered as a sibling of the card's clipping element (the one with
 * `overflow-hidden`), inside a `relative` wrapper, never inside it. Purely
 * decorative chrome: it is inert (`pointer-events-none`), has no hover state, and
 * the label stays real text. Px values on purpose: `html` is 14px here, so rem
 * utilities would shrink the overhang.
 */
export function Ribbon({ label, tone, icon: Icon }: RibbonProps) {
  const { tab, fold } = toneStyles[tone];
  return (
    <span className="pointer-events-none absolute top-3 -right-[6px]">
      <span
        className={`flex items-center gap-1 rounded-l-md py-1 pl-2 pr-[12px] text-[11px] font-bold leading-none text-white shadow-sm ${tab}`}
      >
        {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
        {label}
      </span>
      <span
        aria-hidden="true"
        className={`absolute right-0 top-full h-[6px] w-[6px] [clip-path:polygon(0_0,100%_0,0_100%)] ${fold}`}
      />
    </span>
  );
}
